/**
 * Meridian — Execution-Drift analyzer.
 *
 * Meridian reconciles what the organization committed to with where its energy
 * is actually going. Commitment is the ratified priority ranking; attention is
 * the last 30 days of connector activity flowing through change_events. It
 * surfaces — deterministically — the two ways they diverge:
 *
 *   starved_priority  A top-quartile commitment drawing near-zero linked
 *                     activity while other items get plenty.
 *   attention_sink    An unranked or bottom-half item absorbing an outsized
 *                     share of linked activity.
 *
 * Data grounding:
 *   - Commitment: the latest governance-lane proposal (status approved /
 *     ratified / published) and its ranked_items JSONB — the "Lift" artifact
 *     (migration 248) whose items approve_priority_list stamps with
 *     items.leadership_approved (migrations 246/562). leadership_approved alone
 *     is a boolean and carries no order, so the proposal is the rank source;
 *     with no such proposal there is no ratified ranking and Meridian stays
 *     silent.
 *   - Attention: change_events (migration 400) with source != 'priorities_ai'
 *     (connector events only — internal edits are not execution activity),
 *     joined to items via entity_external_ids (migration 402):
 *     change_events.source = connector_type, change_events.entity_id =
 *     external_id, entity_type = 'item'.
 *
 * The 30-day window is fixed (MERIDIAN_WINDOW): attention *share* is only
 * meaningful over a stable window, so RunScope date ranges are not applied.
 *
 * Detection is exact maths; the LLM only narrates, with deterministic fallback
 * copy that frames every finding as a prompt for inquiry, not an accusation.
 *
 * The pure detection core lives in supabase/functions/_shared/intelligenceCores.ts
 * (shared with the ambient edge function); this file keeps the client-side data
 * loading and batch orchestration.
 */

import { getSupabaseClient } from '../supabaseClient';
import type { RunScope, BatchStats } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';
import { createFinding, clearActiveFindings, type NewFinding } from '../agentFindingsService';
import { narrateFinding } from './llmIntelligenceService';
import {
  detectMeridianFindings,
  MERIDIAN_PERSONA,
  MAX_FINDINGS,
  type RankedCommitment,
  type ActivityCount,
} from '../../../supabase/functions/_shared/intelligenceCores';

export const MERIDIAN_AGENT_TYPE = 'meridian';

// Re-exports so tests and other importers use the same core the runtimes run.
export { detectMeridianFindings };
export type { RankedCommitment, ActivityCount };

const WINDOW_DAYS = 30;

// ── Data loading ─────────────────────────────────────────────────────────────────

async function load(workspaceId: string, _scope: RunScope): Promise<{
  commitments: RankedCommitment[];
  activity: ActivityCount[];
  totalEvents: number;
}> {
  const supabase = getSupabaseClient();

  // ── Commitment: the latest ratified ranking ──
  const { data: proposalRows } = await supabase
    .from('priority_proposals')
    .select('ranked_items, status, updated_at')
    .eq('workspace_id', workspaceId)
    .in('status', ['approved', 'ratified', 'published'])
    .order('updated_at', { ascending: false })
    .limit(1);

  const rankedRaw = (proposalRows?.[0]?.ranked_items ?? []) as Array<Record<string, unknown>>;
  const commitments: RankedCommitment[] = (Array.isArray(rankedRaw) ? rankedRaw : [])
    .filter((r) => typeof r.itemId === 'string' && Number.isFinite(Number(r.rank)))
    .map((r) => ({
      itemId: r.itemId as string,
      title: (r.itemTitle as string) ?? '',
      rank: Number(r.rank),
    }))
    .sort((a, b) => a.rank - b.rank);

  if (commitments.length === 0) return { commitments, activity: [], totalEvents: 0 };

  // Refresh snapshot titles from the live items table.
  const commitmentIds = commitments.map((c) => c.itemId);
  const { data: itemRows } = await supabase
    .from('items')
    .select('id, title')
    .in('id', commitmentIds);
  const liveTitles = new Map<string, string>();
  for (const r of (itemRows ?? []) as Record<string, unknown>[]) {
    liveTitles.set(r.id as string, (r.title as string) ?? '');
  }
  for (const c of commitments) c.title = liveTitles.get(c.itemId) || c.title || `Item ${c.itemId.slice(0, 8)}`;

  // ── Attention: item links + 30-day connector events ──
  const { data: linkRows } = await supabase
    .from('entity_external_ids')
    .select('internal_id, connector_type, external_id')
    .eq('workspace_id', workspaceId)
    .eq('entity_type', 'item')
    .eq('is_active', true)
    .limit(20000);
  const externalToItem = new Map<string, string>();
  for (const r of (linkRows ?? []) as Record<string, unknown>[]) {
    externalToItem.set(`${r.connector_type as string}:${r.external_id as string}`, r.internal_id as string);
  }

  const since = new Date(Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const { data: eventRows } = await supabase
    .from('change_events')
    .select('source, entity_id')
    .eq('workspace_id', workspaceId)
    .neq('source', 'priorities_ai')
    .gte('created_at', since)
    .limit(20000);

  const commitmentIdSet = new Set(commitmentIds);
  const counts = new Map<string, number>();
  let totalEvents = 0;
  for (const r of (eventRows ?? []) as Record<string, unknown>[]) {
    totalEvents++;
    const source = r.source as string;
    const entityId = r.entity_id as string;
    // Connector events carry the external system's id; map it back to the item.
    // Fall back to a direct match when a connector-sourced event already
    // carries the internal item id.
    const itemId = externalToItem.get(`${source}:${entityId}`)
      ?? (commitmentIdSet.has(entityId) ? entityId : undefined);
    if (!itemId) continue;
    counts.set(itemId, (counts.get(itemId) ?? 0) + 1);
  }

  // Titles for linked items outside the ranking (potential attention sinks).
  const commitTitles = new Map(commitments.map((c) => [c.itemId, c.title]));
  const unrankedIds = [...counts.keys()].filter((id) => !commitTitles.has(id));
  const extraTitles = new Map<string, string>();
  if (unrankedIds.length) {
    const { data: extraRows } = await supabase
      .from('items')
      .select('id, title')
      .in('id', unrankedIds);
    for (const r of (extraRows ?? []) as Record<string, unknown>[]) {
      extraTitles.set(r.id as string, (r.title as string) ?? '');
    }
  }

  const activity: ActivityCount[] = [...counts.entries()].map(([itemId, events30d]) => ({
    itemId,
    title: commitTitles.get(itemId) ?? extraTitles.get(itemId) ?? undefined,
    events30d,
  }));

  return { commitments, activity, totalEvents };
}

// ── Batch runner ─────────────────────────────────────────────────────────────────

export async function runMeridianBatch(
  workspaceId: string,
  scope: RunScope,
  config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };

  const { commitments, activity, totalEvents } = await load(workspaceId, scope);
  stats.processed = commitments.length + activity.length;

  let drafts = detectMeridianFindings(commitments, activity, totalEvents);
  drafts = drafts.sort((a, b) => b.weight - a.weight).slice(0, MAX_FINDINGS);

  if (drafts.length === 0) {
    onProgress(1, 1);
    return stats;
  }

  await clearActiveFindings(workspaceId, MERIDIAN_AGENT_TYPE);

  const total = drafts.length;
  let done = 0;
  for (const draft of drafts) {
    if (signal.aborted) break;

    const narration = await narrateFinding({
      persona: draft.persona ?? MERIDIAN_PERSONA,
      findingKind: draft.findingKind,
      evidence: draft.base.data ?? {},
      systemPrompt: config.systemPrompt,
      model: config.model,
    });

    const finding: NewFinding = {
      ...draft.base,
      summary: narration?.summary || draft.fallbackSummary,
      detail: narration?.detail || draft.fallbackDetail,
    };

    const { error } = await createFinding(workspaceId, MERIDIAN_AGENT_TYPE, finding);
    if (error) stats.errors++; else stats.proposed++;

    done++;
    onProgress(done, total);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  return stats;
}
